/** @format */

import * as React from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import Page from 'src/components/Page';
import { TICKET_MODAL, TICKET_STATUS } from 'src/constants';
import { getTicketDetail } from 'src/slices/tickets';
import { useDispatch, useSelector } from 'src/store';
import UpdateTicketModal from 'src/components/widget/tickets/updateTicketModal';
import { Comment, ProgressBar, TicketDetailSkeleton, TicketInfo } from 'src/components/ticket';
import { Button } from 'src/components';
import Breadcrumb from 'src/components/Breadcrumb';

const startViewTransition = callback => {
  if (document.startViewTransition) {
    document.startViewTransition(() => {
      flushSync(callback);
    });
  } else {
    callback();
  }
};

export default function TicketDetail() {
  const history = useHistory();
  const { t } = useTranslation();
  let { id } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const { ticketDetail, ticketLoading } = useSelector(state => state.tickets);
  const [modalOpened, setModalOpened] = React.useState(null);
  const [displayedContent, setDisplayedContent] = React.useState({ loading: true, detail: ticketDetail || null });
  const prevLoadingRef = React.useRef(true);

  React.useEffect(() => {
    dispatch(getTicketDetail(id));
  }, [dispatch, id]);

  React.useEffect(() => {
    setDisplayedContent({ loading: true, detail: null });
    prevLoadingRef.current = true;
  }, [id]);

  const hasCurrentDetail = ticketDetail && String(ticketDetail.incidentid) === String(id);
  const isLoading = ticketLoading || !hasCurrentDetail;

  React.useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = isLoading;

    if (wasLoading && !isLoading && hasCurrentDetail) {
      startViewTransition(() => {
        setDisplayedContent({ loading: false, detail: ticketDetail });
      });
    } else if (isLoading && !wasLoading) {
      setDisplayedContent({ loading: true, detail: ticketDetail });
    } else if (!isLoading && hasCurrentDetail) {
      setDisplayedContent({ loading: false, detail: ticketDetail });
    }
  }, [id, isLoading, hasCurrentDetail, ticketDetail]);

  const ticket = displayedContent.detail;
  const displayedHasCurrentDetail = ticket && String(ticket.incidentid) === String(id);

  const toggleUpdateTicketModal = () => {
    if (modalOpened === TICKET_MODAL.UPDATE_TICKET) {
      setModalOpened(null);
    } else {
      setModalOpened(TICKET_MODAL.UPDATE_TICKET);
    }
  };
  const handleBack = e => {
    e.preventDefault();
    if (typeof location.key !== 'undefined') history.goBack();
    else if (location.pathname.includes('company')) history.push(`/mycompanycases`);
    else history.push(`/mycases`);
  };

  const canAddComment = React.useMemo(() => {
    if (ticket) {
      if (ticket.status === TICKET_STATUS.cancelled) return false;
      if (ticket.status === TICKET_STATUS.closed) return false;
      return true;
    }
    return false;
  }, [ticket]);

  return (
    <Page className={`tickets`} title="Tickets">
      <Breadcrumb
        data={[{ title: t('supportServices'), onClick: handleBack }, { title: ticket?.ticketnumber || '' }]}
      />
      <div className="container">
        <div style={{ viewTransitionName: 'ticket-detail' }}>
          {displayedContent.loading && !displayedHasCurrentDetail ? (
            <TicketDetailSkeleton />
          ) : (
            <div
              style={
                displayedContent.loading
                  ? {
                      opacity: 0.2,
                      transition: 'opacity 180ms ease-in-out',
                      pointerEvents: 'none',
                      cursor: 'not-allowed'
                    }
                  : undefined
              }
            >
              {ticket && (
                <>
                  <h1 className="tickets-title">{ticket.ticketnumber}</h1>
                  <h2 className="tickets-sub-title">{t('Tickets.caseDetail')}</h2>
                  <ProgressBar data={ticket} />
                  <div className="ticket-main">
                    <TicketInfo data={ticket} />
                    <div className="ticket-right-side">
                      <div className="ticket-shadow-box ticket-comments">
                        <p>
                          <strong>{t('Tickets.comments')}</strong>
                        </p>
                        <div className="comments-list">
                          {[...ticket.comments]
                            .sort((a, b) => new Date(a.createdon) - new Date(b.createdon))
                            .map((data, index) => (
                              <Comment data={data} key={index} />
                            ))}
                        </div>
                        {canAddComment && (
                          <div className="comment-btn">
                            <button onClick={toggleUpdateTicketModal}>
                              <span>{t('Tickets.newComment')}</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {canAddComment && (
                    <UpdateTicketModal
                      incidentid={ticket.incidentid}
                      ticketnumber={ticket.ticketnumber}
                      open={modalOpened === TICKET_MODAL.UPDATE_TICKET}
                      onClose={() => setModalOpened(null)}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <Button onClick={handleBack} borderOnly noArrow secondary label={t('backPreviousPage')} />
      </div>
    </Page>
  );
}
